import {
  AlertCircle,
  Building, Calendar, CheckCircle2, DollarSign, Edit3, FileText, Heart, Mail, MapPin, MoreVertical, Phone, Plus, Shield
} from 'lucide-react';
import React, { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  AddressContext,
  AddressProvider, IdentityContext, IdentityProvider
} from '../../context';
import './style.scss';

export function PersonPage() {
  const { personId } = useParams<{ personId: string }>();
  console.log('PersonPage', personId);

  return (
    <IdentityProvider>
      <AddressProvider >
        <PersonPageContent />
      </AddressProvider>
    </IdentityProvider>
  );
}

function getInitials(person) {
  if (!person) return '';

  return [ person.firstName, person.middleName, person.lastName ].filter(Boolean).map(name => name[0]).join('');
}

function getFullName(person) {
  if (!person) return '';

  return [ person.firstName, person.middleName, person.lastName ].filter(Boolean).join(' ');
}

function getAddressString(address) {
  if (!address) return '';

  return [ address.address1, address.address2, address.city, address.state, address.zip, address.country ].filter(Boolean).join(', ');
}

function PersonPageContent() {
  const [ activeTab, setActiveTab ] = useState('assignments');
  const { person } = useContext(IdentityContext);
  const { address } = useContext(AddressContext);

  const initials = getInitials(person);
  const fullName = getFullName(person);
  const addressString = getAddressString(address);
  const dateAdded = (new Date(person.createdDatetime)).toDateString();

  // Document types with their assignments
  const documentTypes = [
    {
      id: 'will',
      name: 'Will',
      icon: FileText,
      color: '#3b82f6',
      assignments: [
        { role: 'Primary Executor', assigned: true, priority: 1 },
        { role: 'Backup Executor', assigned: false, priority: null },
        { role: 'Beneficiary', assigned: true, priority: 2 }
      ],
      status: 'active',
      lastUpdated: '2 days ago'
    },
    {
      id: 'healthcare',
      name: 'Healthcare Directive',
      icon: Heart,
      color: '#ef4444',
      assignments: [
        { role: 'Primary Agent', assigned: false, priority: null },
        { role: 'Backup Agent', assigned: true, priority: 2 },
        { role: 'Notification Contact', assigned: true, priority: 1 }
      ],
      status: 'draft',
      lastUpdated: '1 week ago'
    },
    {
      id: 'poa',
      name: 'Power of Attorney',
      icon: Shield,
      color: '#10b981',
      assignments: [
        { role: 'Primary Agent', assigned: true, priority: 1 },
        { role: 'Backup Agent', assigned: false, priority: null },
        { role: 'Successor Agent', assigned: false, priority: null }
      ],
      status: 'active',
      lastUpdated: '3 days ago'
    },
    {
      id: 'funeral',
      name: 'Funeral Arrangements',
      icon: Calendar,
      color: '#8b5cf6',
      assignments: [
        { role: 'Primary Contact', assigned: true, priority: 1 },
        { role: 'Backup Contact', assigned: false, priority: null },
        { role: 'Decision Maker', assigned: false, priority: null }
      ],
      status: 'draft',
      lastUpdated: '2 weeks ago'
    },
    {
      id: 'survivors',
      name: 'Letter to Survivors',
      icon: Mail,
      color: '#f59e0b',
      assignments: [
        { role: 'Primary Recipient', assigned: true, priority: 1 },
        { role: 'Copy Recipient', assigned: false, priority: null }
      ],
      status: 'active',
      lastUpdated: '1 day ago'
    },
    {
      id: 'trust',
      name: 'Living Trust',
      icon: Building,
      color: '#06b6d4',
      assignments: [
        { role: 'Trustee', assigned: false, priority: null },
        { role: 'Successor Trustee', assigned: true, priority: 1 },
        { role: 'Beneficiary', assigned: true, priority: 2 }
      ],
      status: 'planning',
      lastUpdated: '1 month ago'
    },
    {
      id: 'estate',
      name: 'Estate Planning',
      icon: DollarSign,
      color: '#84cc16',
      assignments: [
        { role: 'Estate Administrator', assigned: false, priority: null },
        { role: 'Financial Contact', assigned: true, priority: 1 }
      ],
      status: 'draft',
      lastUpdated: '5 days ago'
    },
    {
      id: 'financial',
      name: 'Financial Documents',
      icon: Building,
      color: '#f97316',
      assignments: [
        { role: 'Account Contact', assigned: true, priority: 1 },
        { role: 'Emergency Contact', assigned: true, priority: 1 }
      ],
      status: 'active',
      lastUpdated: '1 week ago'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'draft': return '#f59e0b';
      case 'planning': return '#64748b';
      default: return '#64748b';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'draft': return 'Draft';
      case 'planning': return 'Planning';
      default: return 'Unknown';
    }
  };

  const assignedDocuments = documentTypes.filter(doc => 
    doc.assignments.some(assignment => assignment.assigned)
  );

  const availableRoles = documentTypes.flatMap(doc => 
    doc.assignments
      .filter(assignment => !assignment.assigned)
      .map(assignment => ({
        document: doc.name,
        documentId: doc.id,
        role: assignment.role,
        icon: doc.icon,
        color: doc.color
      }))
  );

  return (
    <main className="person-page screen">
      {/* Person Profile */}
      <div className="person-profile">
        <div className="profile-header">
          <div className="avatar-large">{initials}</div>
          <div className="profile-info">
            <h1 className="profile-name">{fullName}</h1>
            <p className="profile-relationship">{person.relationship}</p>
              
            <div className="completion-section">
              <div className="completion-bar">
                <div 
                  className="completion-fill" 
                  style={{ width: `${person.profileCompletion}%` }}
                />
              </div>
              <span className="completion-text">{person.profileCompletion}% Complete</span>
            </div>
              
            <div className="profile-actions">
              <button className="action-button primary">
                <Edit3 size={16} />
                  Edit Profile
              </button>
              <button className="action-button">
                <MoreVertical size={16} />
                  More
              </button>
            </div>
          </div>
        </div>

        <div className="contact-grid">
          <div className="contact-item">
            <Phone className="contact-icon" size={16} />
            <div className="contact-info">
              <div className="contact-label">Phone</div>
              <div className="contact-value">{person.contactPhone}</div>
            </div>
          </div>
          <div className="contact-item">
            <Mail className="contact-icon" size={16} />
            <div className="contact-info">
              <div className="contact-label">Email</div>
              <div className="contact-value">{person.contactEmail}</div>
            </div>
          </div>
          <div className="contact-item">
            <MapPin className="contact-icon" size={16} />
            <div className="contact-info">
              <div className="contact-label">Address</div>
              <div className="contact-value">{addressString}</div>
            </div>
          </div>
          <div className="contact-item">
            <Calendar className="contact-icon" size={16} />
            <div className="contact-info">
              <div className="contact-label">Added</div>
              <div className="contact-value">{dateAdded}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      {/* <div className="tabs">
        <button 
          className={`tab ${activeTab === 'assignments' ? 'active' : ''}`}
          onClick={() => setActiveTab('assignments')}
        >
            Document Assignments ({assignedDocuments.length})
        </button>
        <button 
          className={`tab ${activeTab === 'available' ? 'active' : ''}`}
          onClick={() => setActiveTab('available')}
        >
            Available Roles ({availableRoles.length})
        </button>
        <button 
          className={`tab ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
            Notes & Details
        </button>
      </div> */}

      {/* Content Sections */}
      {activeTab === 'assignments' && (
        <div className="content-section">
          <h2 className="section-title">
            <FileText size={20} />
              Document Assignments
          </h2>
          <div className="documents-grid">
            {assignedDocuments.map((doc) => {
              const IconComponent = doc.icon;
              return (
                <div key={doc.id} className="document-card">
                  <div className="document-header">
                    <div className="document-main">
                      <div 
                        className="document-icon"
                        style={{ backgroundColor: doc.color }}
                      >
                        <IconComponent size={20} />
                      </div>
                      <div className="document-info">
                        <h3 className="document-name">{doc.name}</h3>
                        <div className="document-meta">
                          <span 
                            className="status-badge"
                            style={{ 
                              backgroundColor: `${getStatusColor(doc.status)}20`,
                              color: getStatusColor(doc.status)
                            }}
                          >
                            <div 
                              style={{ 
                                width: 6, 
                                height: 6, 
                                borderRadius: '50%', 
                                backgroundColor: getStatusColor(doc.status) 
                              }}
                            />
                            {getStatusLabel(doc.status)}
                          </span>
                          <span>Updated {doc.lastUpdated}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                    
                  <div className="assignments-list">
                    {doc.assignments.map((assignment, index) => (
                      <div 
                        key={index} 
                        className={`assignment-item ${assignment.assigned ? 'assigned' : ''}`}
                      >
                        <span className="assignment-role">{assignment.role}</span>
                        <div className={`assignment-status ${assignment.assigned ? 'assigned' : 'unassigned'}`}>
                          {assignment.assigned ? (
                            <>
                              <CheckCircle2 size={14} />
                                Assigned
                              {assignment.priority && (
                                <span className="priority-badge">#{assignment.priority}</span>
                              )}
                            </>
                          ) : (
                            <>
                              <AlertCircle size={14} />
                                Available
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'available' && (
        <div className="content-section">
          <h2 className="section-title">
            <Plus size={20} />
              Available Roles to Assign
          </h2>
          <div className="available-roles">
            {availableRoles.map((role, index) => {
              const IconComponent = role.icon;
              return (
                <div key={index} className="role-card">
                  <div className="role-info">
                    <div 
                      className="role-icon"
                      style={{ backgroundColor: role.color }}
                    >
                      <IconComponent size={16} />
                    </div>
                    <div className="role-details">
                      <h4 className="role-title">{role.role}</h4>
                      <p className="role-document">{role.document}</p>
                    </div>
                  </div>
                  <button className="assign-button">
                      Assign Role
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="content-section">
          <h2 className="section-title">
            <Edit3 size={20} />
              Notes & Additional Details
          </h2>
          <div className="notes-section">
            <textarea 
              className="notes-textarea"
              placeholder="Add notes about this person, their capabilities, preferences, or any other relevant information..."
              defaultValue={person.notes}
            />
          </div>
        </div>
      )}
    </main>
  );
}